import React, { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useApplicationContext } from "../context/ApplicationContext";
import { performSignOut } from "../security/signOut";

/**
 * UserMenu - Displays user avatar and dropdown menu with profile, settings, and sign out options
 *
 * Reads `displayUser` from application context to avoid prop drilling.
 */
export const UserMenu: React.FC = () => {
  const { displayUser, openHelp } = useApplicationContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  // Extract initials (up to 2 characters)
  const displayUserName = displayUser ? displayUser.toString() : "Guest";
  const initials = displayUserName
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleClose();
    await performSignOut();
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          onClick={openHelp}
          variant="text"
          startIcon={<HelpOutlineIcon fontSize="small" />}
          sx={{
            color: "#3D3C38",
            textTransform: "none",
            fontFamily: "EnterpriseSansVF, Arial, sans-serif",
            fontWeight: 600,
            fontSize: "0.95rem",
            minWidth: "auto",
            px: 1.5,
            py: 0.625,
            borderRadius: "999px",
            "& .MuiButton-startIcon": {
              marginRight: "6px",
            },
            "&:hover": {
              backgroundColor: "#F5F6F7",
            },
          }}
        >
          Help
        </Button>

        <Box
          aria-hidden="true"
          sx={{
            height: "28px",
            borderRight: "1px solid #3D3C38",
          }}
        />

        <Avatar
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleOpen(e as any);
            }
            if (e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onKeyUp={(e) => {
            if (e.key === " ") {
              e.preventDefault();
              handleOpen(e as any);
            }
          }}
          tabIndex={0}
          role="button"
          aria-label="Open user menu"
          sx={{
            width: 36,
            height: 36,
            border: "1.5px solid #3D3C38",
            borderRadius: "50%",
            cursor: "pointer",
            backgroundColor: "#FFF",
            color: "#3D3C38",
            fontWeight: 600,
            fontSize: "0.875rem",
          }}
        >
          {initials}
        </Avatar>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ autoFocusItem: true }}
        PaperProps={{
          sx: {
            boxShadow: "0px 4px 16px 0px #19191A29",
            border: "1px solid #E5E5E6",
            minWidth: 220,
            p: 0,
            mt: "20px",
          },
        }}
      >
        <ListSubheader
          component="div"
          disableSticky
          role="presentation"
          sx={{
            fontFamily: "EnterpriseSansVF, Arial, sans-serif",
            fontWeight: 700,
            color: "#3D3C38",
            fontSize: "1rem",
            px: 2,
            my: 1.5,
            lineHeight: 1.5,
            backgroundColor: "transparent",
          }}
        >
          {displayUserName}
        </ListSubheader>

        <MenuItem
          onClick={handleClose}
          sx={{
            "& .MuiListItemIcon-root": {
              minWidth: 30,
            },
          }}
        >
          <ListItemIcon>
            <PersonOutlinedIcon fontSize="small" sx={{ color: "#3D3C38" }} />
          </ListItemIcon>
          My profile
        </MenuItem>

        <MenuItem
          onClick={handleClose}
          sx={{
            "& .MuiListItemIcon-root": {
              minWidth: 30,
            },
          }}
        >
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" sx={{ color: "#3D3C38" }} />
          </ListItemIcon>
          Settings
        </MenuItem>

        <MenuItem
          onClick={handleClose}
          sx={{
            "& .MuiListItemIcon-root": {
              minWidth: 30,
            },
          }}
        >
          <ListItemIcon>
            <MailOutlinedIcon fontSize="small" sx={{ color: "#3D3C38" }} />
          </ListItemIcon>
          Communication preferences
        </MenuItem>

        <Divider sx={{ my: 0.5, width: "100%" }} />

        <MenuItem
          onClick={handleSignOut}
          sx={{
            "& .MuiListItemIcon-root": {
              minWidth: 30,
            },
          }}
        >
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" sx={{ color: "#3D3C38" }} />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
};
